import React, { useEffect, useMemo, useState } from "react";
import Button from "../../Components/Utils/Button/Button";
import { LibraryFolderNode } from "../../API/Library/LibraryCommands";
import { FolderIcon } from "./LibraryIcons";
import LibraryDialog from "./LibraryDialog";
import { moveDestinations } from "./libraryUtils";
import styles from "./Library.module.css";

export type MoveRequest = {
  title: string;
  fileIds: string[];
  folderIds: string[];
  currentFolderId: string;
};

type MoveFolderDialogProps = {
  request: MoveRequest | null;
  folders: LibraryFolderNode[];
  rootTitle: string;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (request: MoveRequest, destinationFolderId: string) => void;
};

const MoveFolderDialog = ({ request, folders, rootTitle, busy, onClose, onConfirm }: MoveFolderDialogProps) => {
  const destinations = useMemo(
    () => (request ? moveDestinations(folders, request.fileIds, request.folderIds, request.currentFolderId, rootTitle) : []),
    [request, folders, rootTitle]
  );
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const requestKey = request ? `${request.fileIds.join(",")}|${request.folderIds.join(",")}` : "";
  useEffect(() => {
    setDestinationId(null);
  }, [requestKey]);
  const selected = destinationId ?? destinations[0]?.id ?? null;
  const emptyMessage = request?.fileIds.length
    ? "There is no other folder to move this into."
    : "This folder is already in the only available place.";

  return (
    <LibraryDialog
      isVisible={!!request}
      title={request && request.folderIds.length === 0 ? "Move file" : request && request.fileIds.length === 0 ? "Move folder" : "Move items"}
      onClose={onClose}
    >
      <div className={styles.dialogBody}>
        <p className={styles.memberLine}>Choose where to move {request?.title}.</p>
        {destinations.length === 0 && (
          <p className={styles.memberLine}>{emptyMessage}</p>
        )}
        {destinations.length > 0 && (
          <div className={styles.destinationList} role="listbox" aria-label="Destination">
            {destinations.map((destination) => (
              <button
                key={destination.id || "root"}
                type="button"
                role="option"
                aria-selected={selected === destination.id}
                className={`${styles.destinationOption} ${selected === destination.id ? styles.destinationSelected : ""}`}
                style={{ paddingLeft: `${0.7 + destination.depth * 0.9}rem` }}
                onClick={() => setDestinationId(destination.id)}
              >
                <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
                <span>{destination.title}</span>
              </button>
            ))}
          </div>
        )}
        <div className={styles.dialogActions}>
          <Button colour="#fff" textColour="#24394F" border="#d5dde6" clickHandler={onClose}>Cancel</Button>
          <Button
            disabled={busy || selected === null || destinations.length === 0}
            clickHandler={() => {
              if (!request || selected === null) return;
              onConfirm(request, selected);
            }}
          >
            Move
          </Button>
        </div>
      </div>
    </LibraryDialog>
  );
};

export default MoveFolderDialog;
